import React, {useEffect, useMemo, useState} from "react";
import TabChartHeader from "../../components/tabs/TabChartHeader";
import LargeList_Chart from "./inner/LargeList_Chart";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Sector,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const CHART_COLORS = [
    "#8884d8",
    "#ff9aa2",
    "#9ad0f5",
    "#ffb74d",
    "#64b5f6",
    "#81c784",
    "#7e57c2",
    "#4dd0e1",
    "#42a5f5",
    "#ffd54f",
    "#4db6ac",
    "#ba68c8",
    "#a1887f",
    "#90a4ae",
];

const getItemColor = (item, allItems) => {
    const index = allItems.findIndex(
        (row) => (item.id != null && row.id === item.id) || row.name === item.name
    );
    return CHART_COLORS[(index >= 0 ? index : 0) % CHART_COLORS.length];
};

const PIE_RING_THICKNESS = 20; /* outer - inner (%), 원래 72% - 52% */
const PIE_OUTER_RADIUS = 88;

/** Recharts 3: 범례/파이 공통 호버 — shape에서 직접 확대 (activeIndex prop 미지원) */
const renderPieSliceShape = (activeIndex) => (props, index) => {
    const {cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill} = props;
    const isActive = activeIndex === index;
    const expandedOuter =
        isActive && typeof outerRadius === "number" ? outerRadius + 8 : outerRadius;

    return (
        <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={expandedOuter}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
            stroke="none"
        />
    );
};

const TAB_NAMES = [
    "유지관리 자산현황",
    "금일 등록내역",
    "최근 수행업무",
    "미처리업무",
];

const formatYAxis = (value) => {
    if (value >= 1000) return `${Math.round(value / 1000)}k`;
    return value;
};

const truncateLabel = (name, max = 6) => {
    if (!name) return "";
    return name.length > max ? `${name.slice(0, max)}…` : name;
};

function AssetStatusTooltip({active, payload}) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    return (
        <div className="rounded-[4px] border border-[rgba(0,0,0,0.08)] bg-white px-3 py-2 text-[12px] shadow-sm">
            <div className="font-medium text-[#333]">{row.name}</div>
            <div className="text-[#666]">건수 : {row.cnt?.toLocaleString()}</div>
        </div>
    );
}

const LEGEND_COLS = 3;
const LEGEND_ROWS = 3;
const LEGEND_ROW_HEIGHT = 22; /* px — 행당 고정 높이 (3줄 노출 시) */
const LEGEND_COMPACT_ROW_HEIGHT = 20; /* px — 2줄 노출 시(컴팩트) 행 높이 */
const LEGEND_GAP = 4;
/** 화면 너비가 이 값 미만이면 범례를 2줄로 축소해 파이 최소 크기 보장
 *  (FHD/QHD = 2줄, 4K(3840+) = 3줄) */
const LEGEND_COMPACT_VIEWPORT_WIDTH = 3000;
/** 컴팩트 모드에서 한 행에 최대 몇 개까지 둘지 (라벨 가독성 한계) */
const LEGEND_COMPACT_MAX_COLS = 5;

const isCompactViewport = () =>
    typeof window !== "undefined" && window.innerWidth < LEGEND_COMPACT_VIEWPORT_WIDTH;

export function ManagedAssetDoughnut({items, total}) {
    const [activeIndex, setActiveIndex] = useState(undefined);
    const [isCompactLegend, setIsCompactLegend] = useState(isCompactViewport);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const handler = () => setIsCompactLegend(isCompactViewport());
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);

    const pieData = useMemo(
        () =>
            items
                .filter((item) => Number(item.cnt) > 0)
                .sort((a, b) => Number(b.cnt) - Number(a.cnt)),
        [items]
    );

    /** 화면 해상도에 따라 범례 행 수/컬럼 수/폰트 동적 결정
     *  - 컴팩트(FHD/QHD): 2줄 → 파이차트 영역 더 확보
     *  - 그 외(4K+): 3줄 (기본) */
    const legendRows = isCompactLegend ? 2 : LEGEND_ROWS;
    const legendCols = isCompactLegend
        ? Math.min(
              LEGEND_COMPACT_MAX_COLS,
              Math.max(LEGEND_COLS, Math.ceil((items.length || LEGEND_COLS) / legendRows))
          )
        : LEGEND_COLS;
    const legendSlotCount = legendRows * legendCols;
    const legendRowHeight = isCompactLegend ? LEGEND_COMPACT_ROW_HEIGHT : LEGEND_ROW_HEIGHT;
    const legendFontSizePx = isCompactLegend ? 10 : 11;

    const showEllipsis = pieData.length > legendSlotCount;

    /** 범례 칸 채우기: cnt>0 우선(건수순) → 남는 칸은 cnt=0 항목으로 채움 */
    const legendEntries = useMemo(() => {
        if (showEllipsis) {
            return [
                ...pieData.slice(0, legendSlotCount - 1).map((item, pieIndex) => ({
                    item,
                    pieIndex,
                    hasSlice: true,
                })),
                {type: "ellipsis"},
            ];
        }

        const entries = pieData.map((item, pieIndex) => ({
            item,
            pieIndex,
            hasSlice: true,
        }));

        const zeroItems = items.filter((item) => Number(item.cnt) === 0);
        for (const item of zeroItems) {
            if (entries.length >= legendSlotCount) break;
            entries.push({item, pieIndex: -1, hasSlice: false});
        }

        return entries;
    }, [pieData, items, showEllipsis, legendSlotCount]);

    const legendAreaHeight =
        legendRowHeight * legendRows + LEGEND_GAP * (legendRows - 1);
    const pieShape = useMemo(() => renderPieSliceShape(activeIndex), [activeIndex]);

    useEffect(() => {
        setActiveIndex(undefined);
    }, [items]);

    if (pieData.length === 0) {
        return (
            <div className="flex h-full items-center justify-center bg-[#fafafa] text-[13px] text-[#999]">
                No Data.
            </div>
        );
    }

    return (
        <div
            className="flex h-full w-full flex-col"
            onMouseLeave={() => setActiveIndex(undefined)}
        >
            <div className="flex min-h-0 flex-[4] items-center justify-center">
                <div className="aspect-square h-full max-h-full w-full max-w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{top: 0, right: 0, bottom: 0, left: 0}}>
                            <Tooltip content={<AssetStatusTooltip/>}/>
                            <Pie
                                data={pieData}
                                dataKey="cnt"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={`${PIE_OUTER_RADIUS - PIE_RING_THICKNESS}%`}
                                outerRadius={`${PIE_OUTER_RADIUS}%`}
                                paddingAngle={0}
                                shape={pieShape}
                                onMouseEnter={(_, index) => setActiveIndex(index)}
                                style={{cursor: "pointer"}}
                                isAnimationActive={false}
                            >
                                {pieData.map((entry) => (
                                    <Cell
                                        key={`${entry.id ?? entry.name}`}
                                        fill={getItemColor(entry, items)}
                                        stroke="none"
                                    />
                                ))}
                            </Pie>
                            <text
                                x="50%"
                                y="50%"
                                textAnchor="middle"
                                dominantBaseline="central"
                                fill="#333"
                                fontSize="24"
                                fontWeight="900"
                                style={{fontFamily: "inherit"}}
                            >
                                {total.toLocaleString()}
                            </text>
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
            {/* 범례: 컨테이너 높이에 따라 2~3줄 × 동적 컬럼, 슬롯 초과 시 마지막 칸은 ... */}
            <div
                className="shrink-0 px-2 pb-1"
                style={{minHeight: legendAreaHeight, height: legendAreaHeight}}
            >
                <div
                    className="grid gap-x-2 mx-auto"
                    style={{
                        gridTemplateColumns: `repeat(${legendCols}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${legendRows}, ${legendRowHeight}px)`,
                        rowGap: LEGEND_GAP,
                        maxWidth: legendCols > LEGEND_COLS ? "100%" : 350,
                    }}
                >
                    {legendEntries.map((entry) => {
                        if (entry.type === "ellipsis") {
                            return (
                                <div
                                    key="legend-ellipsis"
                                    className="flex min-w-0 items-center justify-center font-semibold text-[#888]"
                                    style={{
                                        minHeight: legendRowHeight,
                                        height: legendRowHeight,
                                        fontSize: legendFontSizePx,
                                    }}
                                    title={`외 ${pieData.length - (legendSlotCount - 1)}개 항목`}
                                >
                                    ...
                                </div>
                            );
                        }

                        const {item, pieIndex, hasSlice} = entry;
                        const isHovered = hasSlice && activeIndex === pieIndex;
                        const legendLabel = `${item.name} (${Number(item.cnt).toLocaleString()})`;

                        return (
                            <div
                                key={`legend-${item.id ?? item.name}`}
                                role="presentation"
                                className={[
                                    "flex min-w-0 w-full items-center gap-1 rounded-[4px] px-0.5 leading-[14px]",
                                    hasSlice ? "cursor-pointer" : "cursor-default",
                                    isHovered ? "font-semibold text-[#333]" : "text-[#666]",
                                ].join(" ")}
                                style={{
                                    minHeight: legendRowHeight,
                                    height: legendRowHeight,
                                    fontSize: legendFontSizePx,
                                }}
                                onMouseEnter={() => {
                                    if (hasSlice) setActiveIndex(pieIndex);
                                }}
                            >
                                <span
                                    className="inline-block h-2 w-2 shrink-0 rounded-full transition-transform duration-150"
                                    style={{
                                        backgroundColor: getItemColor(item, items),
                                        transform: isHovered ? "scale(1.4)" : "scale(1)",
                                        opacity: hasSlice ? 1 : 0.45,
                                    }}
                                />
                                <span
                                    className="min-w-0 flex-1 truncate"
                                    title={legendLabel}
                                >
                                    {legendLabel}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export function ManagedAssetLineArea({items}) {
    const chartData = useMemo(() => [...items], [items]);
    const yMax = useMemo(() => {
        const maxCnt = Math.max(...chartData.map((d) => Number(d.cnt) || 0), 0);
        if (maxCnt <= 0) return 10;
        return Math.ceil(maxCnt / 500) * 500;
    }, [chartData]);

    if (!chartData.some((item) => Number(item.cnt) > 0)) {
        return (
            <div className="flex h-full items-center justify-center bg-[#fafafa] text-[13px] text-[#999]">
                No Data.
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col">
            <div className="min-h-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{top: 12, right: 16, left: 4, bottom: 0}}>
                        <defs>
                            <linearGradient id="managedAssetAreaGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#6B5CA5" stopOpacity={0.35}/>
                                <stop offset="100%" stopColor="#6B5CA5" stopOpacity={0.03}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="4 4" vertical horizontal stroke="#e8e8e8"/>
                        <XAxis
                            dataKey="name"
                            tick={{fontSize: 10, fill: "#888"}}
                            tickFormatter={(value) => truncateLabel(value, 5)}
                            interval={0}
                            angle={-35}
                            textAnchor="end"
                            height={30}
                            tickMargin={2}
                            axisLine={{stroke: "#666666", strokeWidth: 1}}
                            tickLine={false}
                        />
                        <YAxis
                            width={36}
                            tick={{fontSize: 11, fill: "#888"}}
                            tickFormatter={formatYAxis}
                            domain={[0, yMax]}
                            allowDecimals={false}
                            axisLine={{stroke: "#666666", strokeWidth: 1}}
                            tickLine={false}
                        />
                        <Tooltip content={<AssetStatusTooltip/>}/>
                        <Area
                            type="monotone"
                            dataKey="cnt"
                            stroke="#6B5CA5"
                            strokeWidth={2}
                            fill="url(#managedAssetAreaGradient)"
                            dot={{r: 4, fill: "#6B5CA5", stroke: "#fff", strokeWidth: 2}}
                            activeDot={{r: 5}}
                            name="DATA"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            <div className="flex shrink-0 items-center justify-center gap-2 pt-0.5 text-[11px] leading-none text-[#888]">
                <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-[2px] w-4 rounded bg-[#6B5CA5]"/>
                    <span className="inline-block h-2 w-2 rounded-full border-2 border-[#6B5CA5] bg-white"/>
                    DATA
                </span>
            </div>
        </div>
    );
}

/**
 * 유지관리 자산현황 대시보드 (도넛 + 영역 라인 차트, 탭 전환)
 * @param {Object} data - API 응답 객체 (유지관리 자산현황, 금일 등록내역, ...)
 * @param {boolean} loading - 데이터 조회 중 여부
 */
export function ManagedAssetStatic({data, loading = false}) {
    if (!data) {
        return (
            <div className="managed-asset-static relative flex h-full min-h-0 flex-1 w-full flex-col bg-white">
                {loading && <ChartLoadingOverlay/>}
            </div>
        );
    }

    return <ManagedAssetStaticContent data={data}/>;
}

function ManagedAssetStaticContent({data}) {
    const [tabAttribute, setTabAttribute] = useState(
        TAB_NAMES.map((name, index) => ({clicked: index === 0, name}))
    );

    const activeTab = tabAttribute.find((tab) => tab.clicked);
    const activeTabName = activeTab?.name ?? TAB_NAMES[0];
    const activeData = data[activeTabName] ?? [];

    const activeTotal = useMemo(
        () => activeData.reduce((sum, item) => sum + (Number(item.cnt) || 0), 0),
        [activeData]
    );

    /** 금일 등록내역 / 최근 수행업무 / 미처리업무 는 모두 목록 형태로 표시
     *  (유지관리 자산현황만 도넛 + 영역 차트 view) */
    const showListView = activeTabName !== TAB_NAMES[0];

    return (
        <div className="managed-asset-static flex h-full min-h-0 flex-1 w-full flex-col bg-white [&_*:focus]:outline-none [&_.recharts-active-dot]:outline-none">
            <div className="shrink-0">
                <TabChartHeader
                    title={activeTabName}
                    variant="split"
                    args={tabAttribute}
                    setArgs={setTabAttribute}
                />
            </div>

            <div className="flex min-h-0 flex-1">
                {showListView ? (
                    <LargeList_Chart data={activeData} title={activeTabName}/>
                ) : (
                    <>
                        <div className="mobile-hidden h-full w-[44%] min-w-0 shrink-0 overflow-hidden">
                            <ManagedAssetDoughnut items={activeData} total={activeTotal}/>
                        </div>
                        <div className="mobile-full h-full min-w-0 flex-1 overflow-hidden">
                            <ManagedAssetLineArea items={activeData}/>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default ManagedAssetStatic;
